import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { skipToken } from '@reduxjs/toolkit/query';
import useGetCategoryByArticleId from '../../hooks/useGetCategoryByArticleId';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  ArticleReference,
  useGetApiArticlesReferencesQuery,
  useGetApiNotesByVersionIdQuery,
  useGetApiSettingsPublicBySettingTypeKeyQuery,
} from '../../redux/store/api/api';
import {
  selectActiveVersion,
  selectShowMakeLinkSelectionInfo,
  selectShowMakeMarkSelectionInfo,
  selectVersionContentProcessing,
  setShowMakeLinkSelectionInfo,
  setShowMakeMarkSelectionInfo,
} from '../../redux/store/content/slice';
import {
  articleIdUrlParam,
  EDIT_MODE_KEY,
  SEARCH_RESULT,
  settingTypeApplicationName,
  textAreaIdUrlParam,
} from '../../shared/constants';
import { RightKey } from '../../shared/enums';
import { formatToInputDateString } from '../../shared/utils';
import { getMarkIsError } from '../notes/functions';

function VersionAlerts() {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const version = useAppSelector(selectActiveVersion);
  const [editModeIsActive] = useLocalStorage<boolean>(EDIT_MODE_KEY, false);
  const [searchParams] = useSearchParams();
  const areaId: string | null = searchParams.get(textAreaIdUrlParam);
  const articleId: string | null = searchParams.get(articleIdUrlParam);
  const category = useGetCategoryByArticleId(version.articleId);
  const [showIdNotFoundWarning, setShowIdNotFoundWarning] = useState(false);
  const [showOldVersionInfo, setShowOldVersionInfo] = useState(false);
  const [showFutureVersionInfo, setShowFutureVersionInfo] = useState(false);
  const showMakeLinkSelectionInfo = useAppSelector(
    selectShowMakeLinkSelectionInfo,
  );
  const showMakeMarkSelectionInfo = useAppSelector(
    selectShowMakeMarkSelectionInfo,
  );
  const versionContentIsProcessing = useAppSelector(
    selectVersionContentProcessing,
  );

  const [showMarkNeedsActionWarning, setShowMarkNeedsActionWarning] =
    useState(false);
  const [showLinkNeedsActionWarning, setShowLinkNeedsActionWarning] =
    useState(false);

  const { data: applicationNameSetting } =
    useGetApiSettingsPublicBySettingTypeKeyQuery({
      settingTypeKey: settingTypeApplicationName,
    });

  const { data: noteData } = useGetApiNotesByVersionIdQuery(
    version.id
      ? {
          versionId: version.id,
        }
      : skipToken,
  );
  const { data: referenceData } = useGetApiArticlesReferencesQuery(
    articleId && version.isRecentVersion && version.id
      ? {
          articleId,
          versionId: version.id,
        }
      : skipToken,
  );

  const references = useMemo(
    () =>
      referenceData?.resultObject?.articleReferenceLists?.flatMap(
        (rL) => rL.articleReferences,
      ) as ArticleReference[],
    [referenceData],
  );

  // permissions
  const userCanEditUserGroupNote =
    category?.permittedActions?.includes(
      RightKey.RightNotesManagementEditGroupNote,
    ) || false;
  const userCanEditGlobalNote =
    category?.permittedActions?.includes(
      RightKey.RightNotesManagementEditOrganizationNote,
    ) || false;

  useEffect(() => {
    document.title = `${
      applicationNameSetting?.resultObject?.value
        ? `${applicationNameSetting.resultObject.value} - `
        : ''
    }${version.title}`;
  }, [version, applicationNameSetting]);

  useEffect(() => {
    setShowFutureVersionInfo(false);
    setShowOldVersionInfo(false);

    if (
      !version.isRecentVersion &&
      !(
        new Date().getTime() >
          new Date(
            formatToInputDateString(version.publishedFrom || ''),
          ).getTime() ||
        new Date().getTime() >
          new Date(formatToInputDateString(version.validFrom || '')).getTime()
      )
    ) {
      setShowFutureVersionInfo(true);
      return;
    }

    if (
      !version.isRecentVersion &&
      !(
        new Date().getTime() <
          new Date(
            formatToInputDateString(version.publishedFrom || ''),
          ).getTime() ||
        new Date().getTime() <
          new Date(formatToInputDateString(version.validFrom || '')).getTime()
      )
    ) {
      setShowOldVersionInfo(true);
    }
  }, [version]);

  useEffect(() => {
    // Remove old highlighting of a text passage, if it exists.
    Array.from(
      document.body.getElementsByClassName('border border-2 border-primary'),
    )
      .filter((e) => !e.classList.contains(SEARCH_RESULT))
      .forEach((e) => {
        e.classList.remove('border', 'border-2', 'border-primary');
      });
    setShowIdNotFoundWarning(false);

    // Scroll to referenced targetContentAreaId, add border and focus on this element or inform user that it not exists
    if (areaId) {
      const section: HTMLElement | null = document.getElementById(areaId);

      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        section.classList.add('border', 'border-2', 'border-primary');
        section.focus();
      } else {
        setShowIdNotFoundWarning(true);
      }
    }
  }, [areaId]);

  useEffect(() => {
    if (versionContentIsProcessing) {
      return;
    }

    const userNotesWithMarksNotFound = noteData?.resultObject?.userNotes?.some(
      (n) => n.mark && (n.mark?.markInvalid || getMarkIsError(n)),
    );

    const userGroupNotesWithMarksNotFound =
      noteData?.resultObject?.userGroupNotes?.some(
        (n) => n.mark && (n.mark?.markInvalid || getMarkIsError(n)),
      );
    const generalNotesWithMarksNotFound =
      noteData?.resultObject?.generalNotes?.some(
        (n) => n.mark && (n.mark?.markInvalid || getMarkIsError(n)),
      );

    const show =
      userNotesWithMarksNotFound ||
      (userGroupNotesWithMarksNotFound && userCanEditUserGroupNote) ||
      (generalNotesWithMarksNotFound && userCanEditGlobalNote);

    setShowMarkNeedsActionWarning(show ?? false);
  }, [noteData, versionContentIsProcessing]);

  useEffect(() => {
    if (versionContentIsProcessing) {
      return;
    }

    const show =
      references?.some((r) => r?.sourceContentArea?.areaInvalid) ||
      references?.some(
        (r) =>
          r.targetArticleId !== version.articleId &&
          r?.sourceContentArea?.id &&
          !r.sourceContentArea.areaInvalid &&
          !document.querySelector(
            `[id*=${CSS.escape(r.sourceContentArea.id)}]`,
          ),
      );

    setShowLinkNeedsActionWarning(show ?? false);
  }, [references, versionContentIsProcessing]);

  useEffect(() => {
    if (showMakeLinkSelectionInfo && showMakeMarkSelectionInfo) {
      dispatch(setShowMakeMarkSelectionInfo(false));
    }
  }, [showMakeLinkSelectionInfo]);

  useEffect(() => {
    if (showMakeMarkSelectionInfo && showMakeLinkSelectionInfo) {
      dispatch(setShowMakeLinkSelectionInfo(false));
    }
  }, [showMakeMarkSelectionInfo]);

  return (
    <>
      {showOldVersionInfo && (
        <Alert
          aria-label={translation('closeAlert')}
          variant='warning'
          className='mt-3'
          onClose={() => setShowOldVersionInfo(false)}
          dismissible>
          <p className='m-0'>
            <i className='icon-alte_version me-2' aria-hidden />
            {translation('oldVersionInfo')}
          </p>
        </Alert>
      )}
      {showFutureVersionInfo && (
        <Alert
          aria-label={translation('closeAlert')}
          variant='warning'
          className='mt-3'
          onClose={() => setShowFutureVersionInfo(false)}
          dismissible>
          <p className='m-0'>
            <i className='icon-version_future me-2' aria-hidden />
            {translation('futureVersionInfo')}
          </p>
        </Alert>
      )}
      {showMakeLinkSelectionInfo && (
        <Alert
          aria-label={translation('closeAlert')}
          variant='info'
          className='mt-3'
          onClose={() => dispatch(setShowMakeLinkSelectionInfo(false))}
          dismissible>
          <p className='m-0'>{translation('makeLinkSelection')}</p>
        </Alert>
      )}
      {showMakeMarkSelectionInfo && (
        <Alert
          aria-label={translation('closeAlert')}
          variant='info'
          className='mt-3'
          onClose={() => dispatch(setShowMakeMarkSelectionInfo(false))}
          dismissible>
          <p className='m-0'>{translation('makeMarkSelection')}</p>
        </Alert>
      )}
      {showMarkNeedsActionWarning && (
        <Alert
          aria-label={translation('closeAlert')}
          variant='warning'
          className='mt-3'
          onClose={() => setShowMarkNeedsActionWarning(false)}
          dismissible>
          <p className='m-0'>{translation('marksNotFound')}</p>
        </Alert>
      )}
      {showLinkNeedsActionWarning &&
        editModeIsActive &&
        version.isRecentVersion && (
          <Alert
            aria-label={translation('closeAlert')}
            variant='warning'
            className='mt-3'
            onClose={() => setShowLinkNeedsActionWarning(false)}
            dismissible>
            <p className='m-0'>{translation('linksNotFound')}</p>
          </Alert>
        )}
      {showIdNotFoundWarning && (
        <Alert
          aria-label={translation('closeAlert')}
          variant='warning'
          className='mt-3'
          onClose={() => setShowIdNotFoundWarning(false)}
          dismissible>
          <p className='m-0'>
            {translation('textPassageNotFoundInThisVersion')}
          </p>
        </Alert>
      )}
    </>
  );
}

export default VersionAlerts;
